/**
 * Explore default-location resolution.
 *
 * Priority (first match wins): link parameter > remembered pick > signed-in
 * profile location > IP country > none. A candidate only counts when it
 * matches a known location; there is never a silent default country.
 */

export type ExploreLocationSource = 'url' | 'stored' | 'profile' | 'ip' | 'none';

export interface ResolvableLocation {
  location_id: number;
  iso_alpha2: string | null;
}

export interface ExploreLocationInputs {
  urlLocationId: number | null;
  storedLocationId: number | null;
  profileLocationId: number | null;
  ipCountryCode: string | null;
}

export interface ExploreLocationResult {
  locationId: number | null;
  source: ExploreLocationSource;
}

export const EXPLORE_LOCATION_STORAGE_KEY = 'explore.location.v1';
export const IP_LOOKUP_TIMEOUT_MS = 3000;

export function resolveExploreLocation(
  inputs: ExploreLocationInputs,
  locations: ResolvableLocation[],
): ExploreLocationResult {
  const byId = (id: number | null): number | null =>
    id !== null && locations.some((l) => l.location_id === id) ? id : null;

  const url = byId(inputs.urlLocationId);
  if (url !== null) return { locationId: url, source: 'url' };

  const stored = byId(inputs.storedLocationId);
  if (stored !== null) return { locationId: stored, source: 'stored' };

  const profile = byId(inputs.profileLocationId);
  if (profile !== null) return { locationId: profile, source: 'profile' };

  const code = inputs.ipCountryCode?.trim().toUpperCase();
  if (code) {
    const match = locations.find((l) => (l.iso_alpha2 ?? '').toUpperCase() === code);
    if (match) return { locationId: match.location_id, source: 'ip' };
  }

  return { locationId: null, source: 'none' };
}

export function parseLocationId(value: string | null | undefined): number | null {
  if (!value) return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function readStoredLocationId(): number | null {
  try {
    const raw = localStorage.getItem(EXPLORE_LOCATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { locationId?: unknown };
    return typeof parsed.locationId === 'number' ? parsed.locationId : null;
  } catch {
    return null;
  }
}

export function writeStoredLocationId(locationId: number | null): void {
  try {
    if (locationId === null) localStorage.removeItem(EXPLORE_LOCATION_STORAGE_KEY);
    else localStorage.setItem(
      EXPLORE_LOCATION_STORAGE_KEY,
      JSON.stringify({ locationId, savedAt: new Date().toISOString() }),
    );
  } catch {
    // storage unavailable (private mode) — remembering is best-effort
  }
}

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      () => { clearTimeout(timer); resolve(null); },
    );
  });
}
